// Cache utilities to prevent persistent loading loops
// This module handles detection and clearing of problematic cached data
import { debugLog, debugWarn, debugError } from './logger'

const APP_VERSION = '1.0.0'
// Use a static build ID that only changes when we actually want to clear cache
const BUILD_TIMESTAMP = '2025-09-22-v4' // Only increment this when you want to force cache clear
const VERSION_KEY = 'sergio_app_version'
const BUILD_KEY = 'sergio_build_timestamp'
const CACHE_CLEAR_KEY = 'sergio_cache_cleared'
const DEPLOYMENT_CHECK_KEY = 'sergio_last_deployment_check'

export const detectAndClearCorruptedCache = () => {
  try {
    debugLog('🔍 Checking for cache corruption and deployment changes...')

    const storedVersion = localStorage.getItem(VERSION_KEY)
    const storedBuild = localStorage.getItem(BUILD_KEY)
    // Get last cache clear time (for potential future use)
    // const lastCacheClear = localStorage.getItem(CACHE_CLEAR_KEY)

    // Initialize cache tracking values first if they don't exist
    if (!storedVersion) {
      localStorage.setItem(VERSION_KEY, APP_VERSION)
    }
    if (!storedBuild) {
      localStorage.setItem(BUILD_KEY, BUILD_TIMESTAMP)
    }

    // Only clear cache when build timestamp actually changes (manual control)
    // Don't clear cache on every page load - only when we explicitly update BUILD_TIMESTAMP
    const needsCacheClear = shouldClearCache()

    if (needsCacheClear) {
      debugLog('🧹 Clearing cache due to deployment/corruption detection...')
      clearAllAppData()
      localStorage.setItem(VERSION_KEY, APP_VERSION)
      localStorage.setItem(BUILD_KEY, BUILD_TIMESTAMP)
      localStorage.setItem(CACHE_CLEAR_KEY, Date.now().toString())
      localStorage.setItem(DEPLOYMENT_CHECK_KEY, Date.now().toString())

      debugLog('✅ Cache cleared successfully - no auto-reload')
      return true
    }

    debugLog('✅ Cache is clean, no clearing needed')
    return false
  } catch (error) {
    debugError('❌ Error in cache detection:', error)
    // If there's an error accessing storage, just clear data but don't auto-reload
    clearAllAppData()
    return true
  }
}

const shouldClearCache = () => {
  try {
    // Check if this is the first time we're setting up cache tracking
    const storedVersion = localStorage.getItem(VERSION_KEY)
    const storedBuild = localStorage.getItem(BUILD_KEY)

    // If no stored values exist, this is a first-time setup - don't clear
    if (!storedVersion && !storedBuild) {
      debugLog('🆕 First-time cache setup - initializing only')
      return false
    }

    // ONLY clear cache if we have explicit version mismatch (manual deployment)
    // Don't clear on build timestamp changes - too aggressive for normal reloads
    if (storedVersion && storedVersion !== APP_VERSION) {
      debugLog('🔄 Version change detected:', storedVersion, '->', APP_VERSION)
      return true
    }

    // Check for explicit cache bust parameter in URL
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.has('_cacheBust') || urlParams.has('_loadingTimeout')) {
      debugLog('🔄 Explicit cache bust requested via URL parameter')
      return true
    }

    // Don't clear cache based on build timestamp or reload patterns
    debugLog('✅ Cache check passed - no clearing needed')
    return false
  } catch (error) {
    debugError('❌ Error in shouldClearCache:', error)
    return false // When in doubt, don't clear cache
  }
}

export const clearAllAppData = () => {
  try {
    debugLog('🗑️ Clearing all application data...')

    // Clear localStorage (except critical tracking keys and cookie consent)
    const keysToKeep = [
      VERSION_KEY,
      BUILD_KEY,
      CACHE_CLEAR_KEY,
      DEPLOYMENT_CHECK_KEY,
      'sergio-cookie-consent',
      'i18nextLng', // Keep language preference
      'sergio-theme-mode' // Keep theme preference
    ]
    const localStorageKeys = Object.keys(localStorage)

    localStorageKeys.forEach(key => {
      if (!keysToKeep.includes(key)) {
        // Give special attention to Supabase auth keys
        if (key.startsWith('sb-') || key.includes('supabase')) {
          debugLog('🗑️ Clearing potentially corrupted auth key:', key)
        }
        localStorage.removeItem(key)
      }
    })

    // Clear sessionStorage completely
    sessionStorage.clear()

    // NOTE: React Query uses MEMORY-ONLY cache (no localStorage persistence)
    // However, clear any legacy React Query localStorage keys if they exist
    try {
      const legacyQueryKeys = [
        'REACT_QUERY_OFFLINE_CACHE',
        'react-query-client-cache',
        'auth_state_cache' // REMOVED: No longer used
      ]

      legacyQueryKeys.forEach(key => {
        localStorage.removeItem(key)
      })

      // Clear any tanstack query cache keys (from old persistence setup)
      localStorageKeys.forEach(key => {
        if ((key.includes('query') || key.includes('tanstack') || key.includes('react-query'))
            && !keysToKeep.includes(key)) {
          localStorage.removeItem(key)
        }
      })
    } catch (e) {
      debugWarn('Could not clear legacy React Query cache:', e)
    }

    // Clear any custom service type cache
    localStorage.removeItem('customServiceTypes')

    // Clear IndexedDB caches if available
    try {
      if ('indexedDB' in window) {
        // Clear common cache database names
        const dbNames = ['workbox-precache', 'workbox-runtime', 'CacheStorage']
        dbNames.forEach(name => {
          try {
            indexedDB.deleteDatabase(name)
          } catch (e) {
            debugWarn(`Could not clear IndexedDB ${name}:`, e)
          }
        })
      }
    } catch (e) {
      debugWarn('Could not clear IndexedDB:', e)
    }

    // Clear service worker caches if available (HTTP cache, not app state)
    try {
      if ('caches' in window) {
        caches.keys().then(names => {
          names.forEach(name => {
            debugLog('🗑️ Clearing HTTP cache:', name)
            caches.delete(name)
          })
        })
      }
    } catch (e) {
      debugWarn('Could not clear service worker caches:', e)
    }

    debugLog('✅ All application data cleared')
  } catch (error) {
    debugError('❌ Error clearing application data:', error)
  }
}

export const addCacheDebugInfo = () => {
  debugLog('📊 Cache Debug Info:')
  debugLog('- App Version:', APP_VERSION)
  debugLog('- Build Timestamp:', BUILD_TIMESTAMP)
  debugLog('- Stored Version:', localStorage.getItem(VERSION_KEY))
  debugLog('- Stored Build:', localStorage.getItem(BUILD_KEY))
  debugLog('- Last Cache Clear:', new Date(parseInt(localStorage.getItem(CACHE_CLEAR_KEY) || '0')))
  debugLog('- Last Deployment Check:', new Date(parseInt(localStorage.getItem(DEPLOYMENT_CHECK_KEY) || '0')))
  debugLog('- Current Domain:', window.location.hostname)
  debugLog('- localStorage size:', JSON.stringify(localStorage).length)
  debugLog('- sessionStorage size:', JSON.stringify(sessionStorage).length)
}

export const isStorageCorrupted = () => {
  try {
    // Test localStorage functionality
    const testKey = '__storage_test__'
    localStorage.setItem(testKey, 'test')
    const retrieved = localStorage.getItem(testKey)
    localStorage.removeItem(testKey)

    if (retrieved !== 'test') {
      return true
    }

    // Check for suspiciously large storage
    const storageSize = JSON.stringify(localStorage).length
    if (storageSize > 5 * 1024 * 1024) { // 5MB
      debugWarn('⚠️ localStorage is suspiciously large:', storageSize)
      return true
    }

    // Check for corrupted Supabase auth data
    if (isAuthDataCorrupted()) {
      debugWarn('⚠️ Corrupted Supabase auth data detected')
      return true
    }

    return false
  } catch (error) {
    debugError('Storage corruption detected:', error)
    return true
  }
}

export const isAuthDataCorrupted = () => {
  try {
    // Check if Supabase auth keys exist and are parseable
    const supabaseKeys = Object.keys(localStorage).filter(key =>
      key.startsWith('sb-') || key.includes('supabase')
    )

    for (const key of supabaseKeys) {
      try {
        const value = localStorage.getItem(key)
        if (value) {
          const parsed = JSON.parse(value)

          // Check for specific auth data corruption patterns
          if (typeof parsed === 'object' && parsed !== null) {
            // If it looks like auth data but has corrupted structure
            if (parsed.access_token && typeof parsed.access_token !== 'string') {
              debugWarn('⚠️ Corrupted access_token structure in:', key)
              return true
            }
            if (parsed.refresh_token && typeof parsed.refresh_token !== 'string') {
              debugWarn('⚠️ Corrupted refresh_token structure in:', key)
              return true
            }
          }
        }
      } catch (parseError) {
        debugWarn('⚠️ Unable to parse Supabase auth key:', key, parseError)
        return true
      }
    }

    return false
  } catch (error) {
    debugError('Error checking auth data corruption:', error)
    return true
  }
}